import { Route, Handshake, Beer, Mic, Flag, Briefcase, CalendarDays, MapPin, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Reveal, SectionHead } from "@/components/Reveal";
import { EVENT_TYPES, SAMPLE_EVENTS, imgSrc, imgSrcset } from "@/data/content";

const ICONS = { road: Route, handshake: Handshake, beer: Beer, mic: Mic, flag: Flag, briefcase: Briefcase };

function EventCard({ ev, i }) {
  const Icon = ICONS[ev.icon] || CalendarDays;
  return (
    <Reveal delay={(i % 3) * 0.08}>
      <motion.article
        whileHover={{ y: -8 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="group h-full overflow-hidden rounded-2xl border border-border bg-card transition-colors duration-300 hover:border-gold/50"
        data-testid={`event-card-${ev.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      >
        <div className="relative h-44 overflow-hidden">
          <img src={imgSrc(ev.img, 800)} srcSet={imgSrcset(ev.img)} sizes="(min-width: 1024px) 33vw, 100vw" width={1600} height={1067} alt={ev.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-t from-night/90 via-night/20 to-transparent" />
          <span className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-night/70 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-gold backdrop-blur-md">
            <Icon className="h-3 w-3" /> {ev.type}
          </span>
          <span className="absolute right-4 top-4 rounded border border-white/25 bg-night/60 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-white/70 backdrop-blur-md">
            Exemple
          </span>
        </div>
        <div className="p-6">
          <h3 className="font-heading text-lg font-bold text-foreground group-hover:text-gold">{ev.title}</h3>
          <div className="mt-4 space-y-2 text-sm text-muted-foreground">
            <p className="flex items-center gap-2"><CalendarDays className="h-4 w-4 shrink-0 text-gold" /> {ev.date}</p>
            <p className="flex items-center gap-2"><MapPin className="h-4 w-4 shrink-0 text-gold" /> {ev.place}</p>
          </div>
          <span className="mt-5 block h-px w-full bg-border transition-colors duration-300 group-hover:bg-gold/40" />
          <p className="mt-4 text-xs text-muted-foreground">Réservé aux membres — inscription via l'application.</p>
        </div>
      </motion.article>
    </Reveal>
  );
}

export default function Events({ full = false }) {
  return (
    <section id="evenements" className="py-16 md:py-24 lg:py-32" data-testid="events-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHead
          eyebrow="Événements"
          title="La vie du club, toute l'année."
          sub="Des sorties moto aux rendez-vous business, chaque événement est une occasion de créer du lien et des opportunités."
        />

        <div className="mt-10 flex flex-wrap gap-2.5" data-testid="event-types">
          {EVENT_TYPES.map((t) => {
            const Icon = ICONS[t.icon];
            return (
              <Reveal key={t.label} delay={0.05}>
                <span className="inline-flex items-center gap-2 rounded-full border border-gold/25 bg-gold/5 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-foreground/85 transition-colors duration-300 hover:border-gold hover:text-gold">
                  <Icon className="h-3.5 w-3.5 text-gold" /> {t.label}
                </span>
              </Reveal>
            );
          })}
        </div>

        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {SAMPLE_EVENTS.map((ev, i) => (
            <EventCard key={ev.title} ev={ev} i={i} />
          ))}
        </div>

        <Reveal delay={0.1} className="mt-12 text-center">
          <p className="mb-6 text-xs text-muted-foreground">
            Événements d'exemple présentés pour le design — ils seront remplacés par le vrai agenda du club.
          </p>
          {!full && (
            <Link
              to="/evenements"
              data-testid="events-see-all-button"
              className="btn-sheen group inline-flex items-center gap-3 rounded-full border border-gold/40 px-8 py-4 text-sm font-bold uppercase tracking-[0.14em] text-gold transition-all duration-300 hover:bg-gold hover:text-night"
            >
              Voir tous les événements
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          )}
        </Reveal>
      </div>
    </section>
  );
}
